import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const LessonsYouCanCreateAsAnEslEllTeacherWithCuripod = () => {
  const post = getBlogPost('lessons-you-can-create-as-an-esl-ell-teacher-with-curipod');
  return (
    <BlogArticle post={post}>
      <h2>
        <strong>
          {'✅ Support Multilingual Learners with Engaging, AI-Powered Lessons'}
        </strong>
      </h2>
      <p>
        {
          'Curipod helps ESL/ELL teachers create interactive, standards-aligned lessons that support language development, build confidence, and promote communication. Whether you’re working on vocabulary, sentence structure, grammar, or conversation skills, Curipod makes it easy to generate activities that engage every student—no matter their language level.'
        }
      </p>
      <p>
        {
          'With drawing prompts, polls, writing activities, and real-time feedback, you can create a classroom where all voices are heard and supported.'
        }
      </p>
      <h2>
        <strong>{'🗣️ Lesson Topics ESL/ELL Teachers Can Create with Curipod'}</strong>
      </h2>
      <p>
        {
          'Curipod helps make language learning visible and collaborative. Here are just a few lesson ideas you can create in seconds:'
        }
      </p>
      <h3>
        <strong>{'🧠 Vocabulary & Word Work'}</strong>
      </h3>
      <ul>
        <li>
          {'High-frequency and academic vocabulary'}
          <br />
          <br />
        </li>
        <li>
          {'Categorizing and sorting new words'}
          <br />
          <br />
        </li>
        <li>
          {'Word maps and context clues'}
          <br />
          <br />
        </li>
        <li>
          {'Homophones, synonyms, antonyms'}
          <br />
          <br />
        </li>
        <li>
          {'Sight word practice with drawing prompts'}
          <br />
          <br />
        </li>
      </ul>
      <h3>
        <strong>{'📝 Grammar & Sentence Structure'}</strong>
      </h3>
      <ul>
        <li>
          {'Nouns, verbs, adjectives, and adverbs'}
          <br />
          <br />
        </li>
        <li>
          {'Sentence construction and punctuation'}
          <br />
          <br />
        </li>
        <li>
          {'Subject-verb agreement'}
          <br />
          <br />
        </li>
        <li>
          {'Simple vs. compound sentences'}
          <br />
          <br />
        </li>
        <li>
          {'Editing and revising for clarity'}
          <br />
          <br />
        </li>
      </ul>
      <h3>
        <strong>{'💬 Speaking & Listening'}</strong>
      </h3>
      <ul>
        <li>
          {'Sentence starters and conversation prompts'}
          <br />
          <br />
        </li>
        <li>
          {'Think-pair-share discussions'}
          <br />
          <br />
        </li>
        <li>
          {'Expressing opinions and retelling stories'}
          <br />
          <br />
        </li>
        <li>
          {'Listening comprehension checks'}
          <br />
          <br />
        </li>
        <li>
          {'Peer interviews and roleplay scenarios'}
          <br />
          <br />
        </li>
      </ul>
      <h3>
        <strong>{'🧾 Reading & Writing'}</strong>
      </h3>
      <ul>
        <li>
          {'Identifying main idea and details'}
          <br />
          <br />
        </li>
        <li>
          {'Sequencing events'}
          <br />
          <br />
        </li>
        <li>
          {'Writing personal narratives or responses'}
          <br />
          <br />
        </li>
        <li>
          {'Summarizing text with visuals'}
          <br />
          <br />
        </li>
        <li>
          {'Responding to prompts using sentence frames'}
          <br />
          <br />
        </li>
      </ul>
      <h3>
        <strong>{'🧭 Cultural Connections & SEL'}</strong>
      </h3>
      <ul>
        <li>
          {'Celebrating home languages and traditions'}
          <br />
          <br />
        </li>
        <li>
          {'Exploring identity through writing'}
          <br />
          <br />
        </li>
        <li>
          {'Building classroom belonging and empathy'}
          <br />
          <br />
        </li>
        <li>
          {'Comparing cultural celebrations and routines'}
          <br />
          <br />
        </li>
        <li>
          {'Reflecting on feelings and school experiences'}
          <br />
          <br />
        </li>
      </ul>
      <h2>
        <strong>{'🔧 How to Create an ESL/ELL Lesson with Curipod AI'}</strong>
      </h2>
      <ol>
        <li>
          <Link href="/create" passHref>
            <a>
              {'Click here to create a lesson'}
              <br />
              <br />
            </a>
          </Link>
        </li>
        <li>
          {'Type a prompt like:'}
          <br />
          <br /> <br />
          <ul>
            <li>
              {'“Lesson on describing people using adjectives for ELLs”'}
              <br />
              <br />
            </li>
            <li>
              {'“Writing sentences with ‘because’ for English learners”'}
              <br />
              <br />
            </li>
            <li>
              {'“Vocabulary lesson on school supplies for newcomers”'}
              <br />
              <br />
            </li>
          </ul>
        </li>
        <li>
          <em>{'(Optional)'}</em>
          {' Include your level, language goals, or standards'}
          <br />
          <br />
        </li>
        <li>
          {'Curipod will generate a complete, interactive lesson with:'}
          <br />
          <br /> <br />
          <ul>
            <li>
              {'Visuals, polls, and drawing slides'}
              <br />
              <br />
            </li>
            <li>
              {'Sentence frames and writing supports'}
              <br />
              <br />
            </li>
            <li>
              {'Peer-sharing and open-ended prompts'}
              <br />
              <br />
            </li>
            <li>
              {'Instant AI feedback to support revision and growth'}
              <br />
              <br />
            </li>
          </ul>
        </li>
      </ol>
      <h2>
        <strong>{'🎙️ Built for Language Growth and Belonging'}</strong>
      </h2>
      <p>
        {'Curipod is designed to support multilingual learners in classrooms that are:'}
      </p>
      <ul>
        <li>
          {'Language-rich and visually supported'}
          <br />
          <br />
        </li>
        <li>
          {'Safe spaces for making mistakes and growing'}
          <br />
          <br />
        </li>
        <li>
          {'Focused on conversation, comprehension, and confidence'}
          <br />
          <br />
        </li>
        <li>
          {'Guided by real-time insights into student thinking'}
          <br />
          <br />
        </li>
      </ul>
      <p>{'Want to learn more about our learning philosophy?'}</p>
      <p>
        {'👉'}
        <Link href="/learning-principles" passHref>
          <a>
            {' Explore our learning principles'}
            <br />
          </a>
        </Link>
        {' 👉'}
        <Link href="/c/blog-posts/why-were-betting-on-learning-together" passHref>
          <a>{'Read: Why We’re Betting on Learning Together'}</a>
        </Link>
      </p>
      <h2>
        <strong>{'🔁 Try It Now'}</strong>
      </h2>
      <p>
        {
          'Whether you’re working with newcomers or advanced multilingual learners, Curipod helps you build lessons that spark connection, language development, and student voice.'
        }
      </p>
      <p>
        {'👉'}
        <Link href="/create" passHref>
          <a>{'Create a lesson with Curipod AI'}</a>
        </Link>
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default LessonsYouCanCreateAsAnEslEllTeacherWithCuripod;
